import { FC } from "react";
//#region types
import { CurrencyCode } from "models/enums/currency";
import { SimpleTableRow } from "models/propTypes/simpleTableProps";
import { ReApproveSalaryPreviewProps } from "models/propTypes/wintSalaryPropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Grid, Skeleton } from "@mui/material";
import { SimpleAccordionTable } from "components/common";
import { Description, InlineLabel } from "components/common/wintForms";
import { useReApproveContext } from "../context/useReApproveContext";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";

const ReApproveSalaryPreview: FC<ReApproveSalaryPreviewProps> = (props) => {
  const { personName, currentDeviationDateRange, previousDeviationDateRange, shownChanges } = props;
  const { monthlySalary, privateTax, netSalary, loading } = useReApproveContext();

  const currentDeviationDateRangeValue = DateHelper.formatDateRange(
    currentDeviationDateRange?.start,
    currentDeviationDateRange?.end,
    "-"
  );
  const previousDeviationDateRangeValue = DateHelper.formatDateRange(
    previousDeviationDateRange?.start,
    previousDeviationDateRange?.end,
    "-"
  );

  const changesItems: SimpleTableRow[] = [
    {
      title: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.SalaryDeviation.ReApproveSalaryPreviewChangesCurrentDateRange
      ),
      value: currentDeviationDateRangeValue,
      subContent: [
        {
          title: LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.ReApproveSalaryPreviewChangesPreviousDateRange
          ),
          value: previousDeviationDateRangeValue,
        },
      ],
    },
  ];

  const resultItems: SimpleTableRow[] = [
    {
      title: LocalizationHelperInstance.getFormattedMessage(
        TranslationKeys.SalaryDeviation.ReApproveSalaryPreviewResultEmployeeSalary,
        "",
        {
          personName: personName,
        }
      ),
      value: loading ? (
        <Skeleton variant="text" width="100%" height="24px" />
      ) : (
        LocalizationHelperInstance.getFormattedCurrency(monthlySalary ?? 0, CurrencyCode.SEK)
      ),
      subContent: [
        {
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.NetSalary),
          value: loading ? (
            <Skeleton variant="text" width="100%" height="24px" />
          ) : (
            LocalizationHelperInstance.getFormattedCurrency(netSalary ?? 0, CurrencyCode.SEK)
          ),
        },
        // TODO: Will add later, currently social fee is not included in the salary report
        // {
        //   title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.SocialFees),
        //   value: loading ? (
        //     <Skeleton variant="text" width="100%" height="24px" />
        //   ) : (
        //     LocalizationHelperInstance.getFormattedCurrency(socialFees ?? 0, CurrencyCode.SEK)
        //   ),
        // },
        {
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.Taxes),
          value: loading ? (
            <Skeleton variant="text" width="100%" height="24px" />
          ) : (
            LocalizationHelperInstance.getFormattedCurrency(privateTax ?? 0, CurrencyCode.SEK)
          ),
        },
      ],
    },
  ];

  return (
    <Grid container>
      <Description>
        {LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.SalaryDeviation.ReApproveSalaryPreviewDescription,
          "",
          {
            personName: personName,
          }
        )}
      </Description>
      {shownChanges && changesItems.length > 0 && (
        <>
          <Grid item sx={{ marginTop: "12px" }}>
            <InlineLabel columns={12}>
              {LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.SalaryDeviation.ReApproveSalaryPreviewChangesLabel
              )}
            </InlineLabel>
          </Grid>
          <SimpleAccordionTable items={changesItems} defaultExpanded />
        </>
      )}
      <Grid item sx={{ marginTop: "12px" }}>
        <InlineLabel columns={12}>
          {LocalizationHelperInstance.getFormattedMessage(
            TranslationKeys.SalaryDeviation.ReApproveSalaryPreviewResultLabel
          )}
        </InlineLabel>
      </Grid>
      <SimpleAccordionTable items={resultItems} defaultExpanded />
    </Grid>
  );
};

export default ReApproveSalaryPreview;
